import { TicketType } from '../net/enums';
import type { TicketState } from '../net/codec/ticket';

/** One selectable ticket reference, paired with the ID that must be sent on the wire. */
export interface TicketReferenceOption {
  type: TicketType;
  label: string;
  referenceId: bigint;
  sourceIndex: number;
}

/**
 * Pair the server's three display-string arrays with their matching block in the flat referenceIds
 * array. Keeping this translation outside the UI prevents a selected transaction/payback from being
 * accidentally sent with a game ID when the category arrays have different lengths.
 */
export function ticketReferenceOptions(state: TicketState, type: TicketType): TicketReferenceOption[] {
  let labels: string[];
  let offset: number;

  switch (type) {
    case TicketType.Game:
      labels = state.lastGames;
      offset = 0;
      break;
    case TicketType.Transaction:
      labels = state.lastTransactions;
      offset = state.lastGames.length;
      break;
    case TicketType.Payback:
      labels = state.lastPaybacks;
      offset = state.lastGames.length + state.lastTransactions.length;
      break;
    default:
      return [];
  }

  return labels.map((label, sourceIndex) => ({
    type,
    label,
    referenceId: state.referenceIds[offset + sourceIndex] ?? 0n,
    sourceIndex,
  }));
}

export type TicketDraftError = 'subject' | 'body' | 'reference' | null;

/** Validate the explicit choices required by the redesigned composer before a TicketSave is sent. */
export function validateTicketDraft(
  subject: string,
  body: string,
  type: TicketType,
  availableReferences: number,
  selectedReference: TicketReferenceOption | null,
): TicketDraftError {
  if (!subject.trim()) return 'subject';
  if (!body.trim()) return 'body';
  if (type !== TicketType.General && availableReferences > 0 && selectedReference?.type !== type) {
    return 'reference';
  }
  return null;
}
